import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LocationColorDot } from '@/components/ui/location-color-dot';
import { OptionalRecipeToggles } from '@/components/rsvp/OptionalRecipeToggles';
import { SizeSelector } from '@/components/rsvp/SizeSelector';
import { isRSVPSizeCode } from '@/types/units';
import type { Menu, PickupLocation, PublicRSVPUpdateRequest } from '@/types/api';

export interface PublicRSVPFormValues extends PublicRSVPUpdateRequest {}

interface PublicRSVPFormProps {
  menu: Menu;
  pickupLocations: PickupLocation[];
  initialValues?: Partial<PublicRSVPFormValues>;
  disabled?: boolean;
  submitting?: boolean;
  submitLabel: string;
  onSubmit: (values: PublicRSVPFormValues) => void | Promise<void>;
}

const defaultValues: PublicRSVPFormValues = {
  name: '',
  phone: '',
  pickupLocationId: 0,
  size: 'M',
  mithaas: true,
  optedOutMenuRecipeIds: [],
};

export function PublicRSVPForm({
  menu,
  pickupLocations,
  initialValues,
  disabled = false,
  submitting = false,
  submitLabel,
  onSubmit,
}: PublicRSVPFormProps) {
  const [values, setValues] = useState<PublicRSVPFormValues>(() => ({
    ...defaultValues,
    ...initialValues,
    optedOutMenuRecipeIds: initialValues?.optedOutMenuRecipeIds || [],
  }));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValues({
      ...defaultValues,
      ...initialValues,
      optedOutMenuRecipeIds: initialValues?.optedOutMenuRecipeIds || [],
    });
    setError(null);
  }, [initialValues]);

  const optedOutIds = useMemo(
    () => new Set(values.optedOutMenuRecipeIds || []),
    [values.optedOutMenuRecipeIds]
  );
  const selectedPickupLocation = useMemo(
    () => pickupLocations.find((location) => location.id === values.pickupLocationId) || null,
    [pickupLocations, values.pickupLocationId]
  );

  const optionalRecipeCount = menu.recipes?.filter((recipe) => recipe.isOptional).length || 0;

  const updateValue = <K extends keyof PublicRSVPFormValues>(
    key: K,
    value: PublicRSVPFormValues[K]
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleOptOutChange = (menuRecipeId: number, included: boolean) => {
    setValues((current) => {
      const next = new Set(current.optedOutMenuRecipeIds || []);
      if (included) {
        next.delete(menuRecipeId);
      } else {
        next.add(menuRecipeId);
      }
      return { ...current, optedOutMenuRecipeIds: Array.from(next) };
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!values.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!values.phone.trim()) {
      setError('Phone number is required');
      return;
    }

    if (!values.pickupLocationId) {
      setError('Pickup location is required');
      return;
    }

    await onSubmit({
      ...values,
      name: values.name.trim(),
      phone: values.phone.trim(),
      optedOutMenuRecipeIds: values.optedOutMenuRecipeIds || [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Card className="border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </Card>
      )}

      <div className="space-y-2">
        <Label htmlFor="public-name">Name</Label>
        <Input
          id="public-name"
          value={values.name}
          onChange={(event) => updateValue('name', event.target.value)}
          disabled={disabled || submitting}
          maxLength={255}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="public-phone">Phone</Label>
        <Input
          id="public-phone"
          type="tel"
          value={values.phone}
          onChange={(event) => updateValue('phone', event.target.value)}
          disabled={disabled || submitting}
          maxLength={50}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="public-pickup">Pickup location</Label>
        <Select
          value={values.pickupLocationId ? String(values.pickupLocationId) : undefined}
          onValueChange={(value) => updateValue('pickupLocationId', Number(value))}
          disabled={disabled || submitting}
        >
          <SelectTrigger id="public-pickup" className="w-full">
            <SelectValue placeholder="Select pickup location">
              {selectedPickupLocation ? (
                <span className="flex items-center gap-2">
                  <LocationColorDot colorCode={selectedPickupLocation.colorCode} />
                  <span>{selectedPickupLocation.name}</span>
                </span>
              ) : null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent style={{ width: 'var(--radix-select-trigger-width)' }}>
            {pickupLocations.map((location) => (
              <SelectItem
                key={location.id}
                value={String(location.id)}
                className="items-start whitespace-normal [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1"
              >
                <span className="block min-w-0">
                  <span className="flex min-w-0 items-center gap-2">
                    <LocationColorDot colorCode={location.colorCode} />
                    <span className="min-w-0 break-words">{location.name}</span>
                  </span>
                  {location.address && (
                    <span className="mt-0.5 block min-w-0 break-words pl-5 text-xs leading-snug text-muted-foreground">
                      ({location.address})
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Size</Label>
          <SizeSelector
            value={values.size}
            onChange={(size) => {
              if (isRSVPSizeCode(size)) updateValue('size', size);
            }}
            disabled={disabled || submitting}
            includeSkip={false}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label>Mithaas</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={values.mithaas ? 'default' : 'outline'}
              onClick={() => updateValue('mithaas', true)}
              disabled={disabled || submitting}
              className="flex-1"
              aria-pressed={values.mithaas}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant={!values.mithaas ? 'default' : 'outline'}
              onClick={() => updateValue('mithaas', false)}
              disabled={disabled || submitting}
              className="flex-1"
              aria-pressed={!values.mithaas}
            >
              No
            </Button>
          </div>
        </div>
      </div>

      {optionalRecipeCount > 0 && (
        <div className="rounded-md border p-3">
          <p className="text-sm font-medium">Recipe opt outs</p>
          <OptionalRecipeToggles
            recipes={menu.recipes}
            optedOutIds={optedOutIds}
            onChange={handleOptOutChange}
            disabled={disabled || submitting}
            readOnly={disabled}
          />
        </div>
      )}

      <Button type="submit" className="w-full" disabled={disabled || submitting}>
        {submitting ? 'Saving...' : submitLabel}
      </Button>
    </form>
  );
}
